import { useState } from "react";

import { addDays, formatISO, parse } from "date-fns";
import { ListFilter } from "lucide-react";

import { GetAllFilesApiV1FilesGetData } from "@/api/generated/types.gen";
import { useAppForm } from "@/components/Form";

import { Button } from "@components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";

import { cn } from "@utils/ui";

import {
  DOCUMENT_STATUS_OPTIONS,
  DOCUMENT_TYPES_OPTIONS,
  SORTING_ORDER_OPTIONS,
} from "../contants/documents";
import { FileFilterFormData } from "../schema/filters";

type FileFilterProps = {
  className?: string;
  onFiltersChange?: (filters: GetAllFilesApiV1FilesGetData["query"]) => void;
  isStatusEditable?: boolean;
};

export const FileFilter = ({
  className,
  onFiltersChange,
  isStatusEditable = true,
}: FileFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  const form = useAppForm({
    defaultValues: {
      classification: "",
      status: isStatusEditable ? "" : undefined,
      created_at: "",
      sort_by: "",
    } as FileFilterFormData,
    onSubmit: async ({ value }) => {
      const uploadedDay = value.created_at
        ? parse(value.created_at, "yyyy-MM-dd", new Date())
        : undefined;
      const filterValues: GetAllFilesApiV1FilesGetData["query"] = {
        classification: value.classification || undefined,
        status: isStatusEditable ? value.status || undefined : undefined,
        // MDRS-520: the day's local midnights, each with its own offset — a
        // bare day is read by the backend as a UTC day, which misses the
        // morning's uploads, and across a daylight-saving change the day is
        // 23 or 25 hours, so the backend can't work out its end alone.
        created_at: uploadedDay && formatISO(uploadedDay),
        created_at_end: uploadedDay && formatISO(addDays(uploadedDay, 1)),
        sort_by:
          (value.sort_by as "recently_added" | "recently_updated" | "oldest") ||
          undefined,
      };

      // Call the callback to update filters in parent component
      onFiltersChange?.(filterValues);

      // Check if any filters are applied
      const activeFilters = Object.values(value).some(
        (val) => val && val.trim() !== ""
      );

      setHasActiveFilters(activeFilters);
      setIsOpen(false);
    },
  });

  const handleCancel = () => {
    form.reset();
    onFiltersChange?.({
      classification: undefined,
      status: undefined,
      created_at: undefined,
      created_at_end: undefined,
      sort_by: undefined,
    });
    setHasActiveFilters(false);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          {/* Gray simple-outline by default; switches to the blue
              secondary-subtle variant once filters are active — a real
              variant swap, matching CasesList's Filter trigger, not a
              className color override. */}
          <Button
            variant={hasActiveFilters ? "secondary-subtle" : "simple-outline"}
            size="sm"
            className={cn("h-10 gap-2 transition-all duration-150", className)}
          >
            <ListFilter className="size-4" />
            Filter
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="flex max-h-[var(--radix-popover-content-available-height)] w-96 flex-col overflow-hidden rounded-xl border border-border bg-background-white p-0 shadow-lg"
          align="end"
          side="bottom"
          sideOffset={8}
        >
          <h3 className="flex-shrink-0 border-b border-border px-4 pb-2 pt-4 text-sm font-semibold">
            Filter
          </h3>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-2">
              <form.AppField name="classification">
                {(field) => (
                  <field.SelectField
                    label="Documentation type"
                    labelClassName="text-xs"
                    labelContainerClassName="mb-1"
                    placeholder="Select documentation type"
                    options={DOCUMENT_TYPES_OPTIONS}
                  />
                )}
              </form.AppField>
              {isStatusEditable && (
                <form.AppField name="status">
                  {(field) => (
                    <field.SelectField
                      label="Status"
                      labelClassName="text-xs"
                      labelContainerClassName="mb-1"
                      placeholder="Select status"
                      options={DOCUMENT_STATUS_OPTIONS}
                    />
                  )}
                </form.AppField>
              )}

              <form.AppField name="created_at">
                {(field) => (
                  <field.DateField
                    label="Uploaded date"
                    labelClassName="text-xs"
                    labelContainerClassName="mb-1"
                    placeholder="Select date"
                    dateFormat="yyyy-MM-dd"
                  />
                )}
              </form.AppField>

              <form.AppField name="sort_by">
                {(field) => (
                  <field.SelectField
                    label="Sort by"
                    labelClassName="text-xs"
                    labelContainerClassName="mb-1"
                    placeholder="Select sort order"
                    options={SORTING_ORDER_OPTIONS}
                  />
                )}
              </form.AppField>
            </div>

            <form.AppForm>
              <div className="flex flex-shrink-0 items-center justify-between gap-3 border-t border-border px-4 py-4">
                <button
                  type="button"
                  className="font-head text-xs font-semibold text-foreground-muted underline-offset-2 hover:text-foreground hover:underline"
                  onClick={() => form.reset()}
                >
                  Clear all
                </button>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="simple-outline"
                    size="sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <form.SubmitButton size="sm">Apply filters</form.SubmitButton>
                </div>
              </div>
            </form.AppForm>
          </form>
        </PopoverContent>
      </Popover>
    </div>
  );
};
